
import Vue from "vue";
import { TypedVue } from "@/store/types";
import { Component, Watch, Ref, Prop } from "vue-property-decorator";
import { Modal } from "bootstrap";
import { V2Assessment, V2Project, V2Benchmark, TeamEffectivenessV2ReportData, ProjectReportData, Benchmark as AdminSchemaBenchmark } from "@/store/admin/adminTypes";
import { systemAPI } from "@/lib/s3PlatformApi";
import { Trait, TRAIT_GROUPINGS, TRAITS, TAG_COLOURS } from "@/lib/consts";
import ComparisonRadarChartComponent, { ComparisonAssessmentItem, ComparisonBenchmarkItem, ComparisonChartPreset } from "@/components/admin/ComparisonComponents/ComparisonRadarChartComponent.vue";
import { AssessmentState, CreateProjectReport, SchemasCandidateAssessment, SchemasV2InternalAssessmentsScoring } from "@/lib/serviceTypes";
import { Benchmark, ProjectReportType } from "@/store/internal/internalTypes";
import { ColorMap } from "@/lib/charts/HighChartThemes";
import draggable from "vuedraggable";
import moment from "moment-mini";
import { Action } from "vuex-class";
import { Actions } from "@/store/admin/adminActions";
import { Actions as InternalActions } from "@/store/internal/interalActions";
import TagPicker from "@/components/input/TagPicker.vue";
import ComparisonColourPickerModal from "@/components/admin/ComparisonComponents/ComparisonColourPickerModal.vue";

export const TEAM_EFFECTIVENESS_V2_VERSION = "2.0";

const TEAM_AVERAGE_ID = "team-average";
const SUBGROUP_AVERAGE_PREFIX = "subgroup-average:";
const TEAM_AVERAGE_COLOUR = "#027fb0"; // $blue

@Component({ components: { ComparisonRadarChartComponent, draggable, TagPicker, ComparisonColourPickerModal } })
export default class TeamEffectivenessWizardModal extends TypedVue {
  @Ref("team-effectiveness-wizard-modal")
  readonly modal!: Modal;

  @Prop() show!: boolean;
  @Prop({ default: null }) project!: V2Project;
  @Prop({ default: null }) reportToEdit!: string | null;

  @Action(Actions.SAVE_PROJECT_REPORT, { namespace: "admin" })
  systemSaveProjectReport!: (reportArgs: { projectId: string; projectReport: CreateProjectReport }) => Promise<string>;

  @Action(InternalActions.UPDATE_REPORT, { namespace: "internal" })
  updateProjectReport!: (data: { reportId: string; name: string; description: string | undefined; assessmentIds: string[]; data: Record<string, unknown> }) => Promise<void>;

  @Action(Actions.GET_ORG_PROJECTS, { namespace: "admin" })
  getProjects!: () => Promise<V2Project[]>;

  @Action(Actions.GET_PROJECT_REPORT_DATA, { namespace: "admin" })
  getProjectReportData!: ({ reportId, raw }: { reportId: string; raw?: boolean }) => Promise<ProjectReportData>;

  @Watch("show")
  async onShowChange(show: boolean) {
    if (show) {
      this.modal.show();

      if (this.project) {
        this.assessmentsLoading = true;
        const assessmentsPromise = this.getAssessments().then(() => {
          this.setComparableAssessments();
        });
        const benchmarkPromise = this.getBenchmarks(this.project.identifier).then(() => {
          this.compariableBenchmarks = this.benchmarks.map((benchmark) => ({
            id: benchmark.identifier,
            name: benchmark.name,
            hidden: false,
            isLocked: false,
            selected: benchmark.default || false,
            color: benchmark.colour,
            initialColor: benchmark.colour,
            data: this.mapBenchmarkToSchemaBenchmark(benchmark),
          }));
        });
        const tagsPromise = this.getTags();

        if (this.reportToEdit) {
          this.reportLoading = true;
          this.editMode = true;
          const reportPromise = this.getProjectReportData({ reportId: this.reportToEdit, raw: true }).then((report) => {
            this.initialReport = {
              identifier: report.identifier,
              name: report.name,
              description: report.description,
              version: report.version,
              type: report.type,
              assessmentIds: report.assessmentIds || [],
              data: report.data as TeamEffectivenessV2ReportData,
            };
          });
          await Promise.all([assessmentsPromise, benchmarkPromise, tagsPromise, reportPromise])
            .then(() => {
              this.populateInitialReportData();
            })
            .finally(() => {
              this.reportLoading = false;
              this.assessmentsLoading = false;
            });
        } else {
          this.supportingCompetencies = [...TRAITS];
          await Promise.all([assessmentsPromise, benchmarkPromise, tagsPromise]).finally(() => {
            this.assessmentsLoading = false;
          });
        }
      }
    } else {
      this.modal.hide();
      this.resetState();
    }
  }

  @Watch("selectedAssessmentIds")
  onSelectedAssessmentIdsChange(newValue: string[], oldValue: string[]) {
    this.setComparableAssessments();

    if (newValue.length < oldValue.length) {
      const removedAssessmentIds = oldValue.filter((x) => !newValue.includes(x));
      for (const removedAssessmentId of removedAssessmentIds) {
        for (const preset of this.comparisonPresets) {
          preset.items = preset.items.filter((x) => x.id !== removedAssessmentId);
        }
      }
    }
  }

  @Watch("tags", { deep: true })
  onTagsChange(newVal: Record<string, string[]>) {
    if (!this.settingInitialValues) {
      for (const tagList of Object.values(newVal)) {
        for (const tag of tagList) {
          if (!this.tagColours[tag]) {
            this.tagColours[tag] = TAG_COLOURS[Object.keys(this.tagColours).length % TAG_COLOURS.length];
            this.showColourPicker(tag);
          }
        }
      }
    }
    // Subgroup averages on the radar follow the tags.
    this.setComparableAssessments();
  }

  public step = 1;
  public totalSteps = 5;
  public editMode = false;
  public scoringVerison = "v2";
  public reportVersion = TEAM_EFFECTIVENESS_V2_VERSION;
  public reportType: ProjectReportType = "team-effectiveness";
  public initialReport: {
    identifier: string;
    name: string;
    description?: string;
    version: string;
    type: string;
    assessmentIds?: string[];
    data: TeamEffectivenessV2ReportData;
  } | null = null;
  public assessments: V2Assessment[] = [];
  public benchmarks: V2Benchmark[] = [];
  public candidateSelectedState: Record<string, boolean> = {};

  searchText = "";
  reportName = "";
  reportDescription = "";
  reportOverviewCustomText = "";

  candidateRoles: Record<string, string> = {};
  tags: Record<string, string[]> = {};
  tagColours: Record<string, string> = {};
  tagOptions: string[] = [];
  settingInitialValues = false;
  showColourPickerModal = false;
  selectedColourItem: string | null = null;

  criticalCompetencies: Trait[] = [];
  importantCompetencies: Trait[] = [];
  supportingCompetencies: Trait[] = [];

  compariableBenchmarks = Vue.observable<ComparisonBenchmarkItem[]>([]);
  averageBenchmarks: ComparisonBenchmarkItem[] = [];
  compariableAssessments = Vue.observable<ComparisonAssessmentItem[]>([]);
  comparisonPresets = Vue.observable<ComparisonChartPreset[]>([]);

  generatedReportId: string | null = null;
  assessmentsLoading = false;
  reportLoading = false;
  saving = false;

  get stepName() {
    switch (this.step) {
      case 1:
        return "Name Your Report";
      case 2:
        return "Add Team Members";
      case 3:
        return "Set Competency Priorities";
      case 4:
        return "Select Benchmarks and Presets";
      case 5:
        return this.editMode ? "Review & Update Report" : "Review & Confirm";
      default:
        return "";
    }
  }

  get canProceedToNextStep() {
    if (this.step === 1) {
      return this.reportName.trim().length > 0;
    } else if (this.step === 2) {
      return this.selectedCount > 0;
    } else if (this.step === 3) {
      return this.criticalCompetencies.length === 5 && this.importantCompetencies.length === 5 && this.supportingCompetencies.length === 5;
    }
    return true;
  }

  get validAssessments() {
    return this.assessments
      .filter((assessment) => {
        if (!assessment.report) {
          return false;
        }
        if (!assessment.scores?.find((x) => x.scoringVersion === this.scoringVerison)) {
          return false;
        }
        if (this.searchText) {
          return assessment.candidate.name.toLowerCase().includes(this.searchText.toLowerCase()) || assessment.identifier.slice(0, 4).toLowerCase().includes(this.searchText.toLowerCase());
        }
        return true;
      })
      .sort((a, b) => a.candidate.name.toLowerCase().localeCompare(b.candidate.name.toLowerCase()));
  }

  get selectedAssessmentIds() {
    return Object.keys(this.candidateSelectedState).filter((key) => this.candidateSelectedState[key]);
  }

  set selectedAssessmentIds(value: string[]) {
    this.candidateSelectedState = this.assessments.reduce((acc, assessment) => {
      acc[assessment.identifier] = value.includes(assessment.identifier);
      return acc;
    }, {} as Record<string, boolean>);
  }

  get selectedCount() {
    return Object.values(this.candidateSelectedState).filter((selected) => selected).length;
  }

  get allSelected() {
    return this.validAssessments.length > 0 && this.validAssessments.every((a) => this.candidateSelectedState[a.identifier]);
  }

  get selectedAssessments() {
    return this.assessments.filter((assessment) => this.candidateSelectedState[assessment.identifier]).sort((a, b) => a.candidate.name.toLowerCase().localeCompare(b.candidate.name.toLowerCase()));
  }

  // Team / subgroup averages first, then the project's benchmarks (loaded separately, so kept apart).
  get radarBenchmarks(): ComparisonBenchmarkItem[] {
    return [...this.averageBenchmarks, ...this.compariableBenchmarks];
  }

  get allTags() {
    return Array.from(new Set(Object.values(this.tags).flat().concat(this.tagOptions)));
  }

  get selectedTags() {
    return Array.from(new Set(this.selectedAssessmentIds.flatMap((id) => this.tags[id] ?? [])));
  }

  categoryFor(trait: Trait) {
    return Object.keys(TRAIT_GROUPINGS).find((group) => TRAIT_GROUPINGS[group].includes(trait));
  }

  toggleAll(): void {
    const select = !this.allSelected;
    const next = { ...this.candidateSelectedState };
    this.validAssessments.forEach((a) => (next[a.identifier] = select));
    this.candidateSelectedState = next;
  }

  nextStep(): void {
    if (this.step === this.totalSteps) {
      this.saveReport().catch((error) => {
        console.error("Error saving report:", error);
        this.saving = false;
      });
    }

    if (this.step < this.totalSteps) this.step++;
  }

  prevStep(): void {
    if (this.step > 1) this.step--;
  }

  removePreset(preset: ComparisonChartPreset) {
    const index = this.comparisonPresets.findIndex((x) => x.id === preset.id);
    if (index !== -1) {
      this.comparisonPresets.splice(index, 1);
    }
  }

  savePreset(preset: ComparisonChartPreset) {
    this.comparisonPresets.push(preset);
  }

  updatePreset(preset: ComparisonChartPreset) {
    const index = this.comparisonPresets.findIndex((x) => x.id === preset.id);
    if (index !== -1) {
      this.comparisonPresets.splice(index, 1, preset);
    }
  }

  async getAssessments() {
    this.assessments = (await systemAPI.getAssessments({ include_scores: true })).data;
  }

  async getBenchmarks(projectId: string) {
    this.benchmarks = (await systemAPI.getBenchmarks({ project_id: projectId })).data;
  }

  async getTags() {
    this.tagOptions = (await systemAPI.getReportTags("team-effectiveness")).data;
  }

  private averageTraitScores(scorings: SchemasV2InternalAssessmentsScoring[]): Record<string, number> {
    const out: Record<string, number> = {};
    TRAITS.forEach((t) => {
      const values = scorings.map((s) => (s.traitScores as Record<string, number> | undefined)?.[t]).filter((v): v is number => typeof v === "number");
      out[t] = values.length ? Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 100) / 100 : 0;
    });
    return out;
  }

  // Team and subgroup averages are synthetic benchmarks, so the radar sidebar labels them
  // "Benchmark" and they need no readiness literal. Their ids ("team-average",
  // "subgroup-average:{tag}") are unchanged so existing presets still match them.
  private averageBenchmark(id: string, name: string, colour: string, traitScores: Record<string, number>): ComparisonBenchmarkItem {
    const existing = this.averageBenchmarks.find((x) => x.id === id);
    return {
      id,
      name,
      hidden: false,
      isLocked: false,
      selected: existing?.selected ?? false,
      color: existing?.color ?? colour,
      initialColor: colour,
      data: {
        benchmarkId: id,
        name,
        colour,
        alwaysShow: false,
        traitScores,
        dateCreated: "",
        lastUpdated: "",
      },
    } as ComparisonBenchmarkItem;
  }

  public setComparableAssessments() {
    const selected = this.assessments
      .filter((assessment) => this.selectedAssessmentIds.includes(assessment.identifier))
      .filter((assessment) => assessment.scores?.find((x) => x.scoringVersion === "v2"))
      .map((assessment) => ({ assessment, scoring: assessment.scores?.find((x) => x.scoringVersion === "v2") as SchemasV2InternalAssessmentsScoring }));

    const individuals = selected.map(({ assessment, scoring }) => {
      const existing = this.compariableAssessments.find((x) => x.id === assessment.identifier);
      return {
        id: assessment.identifier,
        name: `${assessment.identifier.substring(0, 4)} - ${assessment.candidate.name}`,
        hidden: false,
        isLocked: false,
        selected: existing?.selected ?? false,
        color: existing?.color ?? ColorMap[this.project.theme][scoring.readiness],
        initialColor: ColorMap[this.project.theme][scoring.readiness],
        data: this.mapAssessmentToSchemaAssessment(assessment, scoring),
      } as ComparisonAssessmentItem;
    });

    // Team + subgroup averages so presets can compare the team (or a subgroup) against leaders/benchmarks.
    const averages: ComparisonBenchmarkItem[] = [];
    if (selected.length) {
      averages.push(this.averageBenchmark(TEAM_AVERAGE_ID, "Team average", TEAM_AVERAGE_COLOUR, this.averageTraitScores(selected.map((s) => s.scoring))));
      this.selectedTags.forEach((tag) => {
        const members = selected.filter((s) => (this.tags[s.assessment.identifier] ?? []).includes(tag));
        if (!members.length) return;
        averages.push(this.averageBenchmark(`${SUBGROUP_AVERAGE_PREFIX}${tag}`, `${tag} average`, this.tagColours[tag] ?? "#5c5e64", this.averageTraitScores(members.map((s) => s.scoring))));
      });
    }

    this.averageBenchmarks = averages;
    this.compariableAssessments = individuals;
  }

  public populateInitialReportData() {
    if (this.initialReport) {
      this.settingInitialValues = true;

      this.editMode = true;
      this.reportName = this.initialReport.name;
      this.reportDescription = this.initialReport.description ?? "";
      this.selectedAssessmentIds = this.initialReport.assessmentIds ?? [];

      const priorities = this.initialReport.data.traitPriorities ?? {};
      this.criticalCompetencies = Object.keys(priorities).filter((t) => priorities[t] === 2) as Trait[];
      this.importantCompetencies = Object.keys(priorities).filter((t) => priorities[t] === 1) as Trait[];
      this.supportingCompetencies = Object.keys(priorities).filter((t) => priorities[t] === 0) as Trait[];
      // Any trait missing from the saved priorities lands in Supporting.
      TRAITS.forEach((t) => {
        if (!(t in priorities)) this.supportingCompetencies.push(t);
      });

      this.tags = this.initialReport.data.tags || {};
      this.tagColours = this.initialReport.data.tagColours || {};
      this.candidateRoles = this.initialReport.data.candidateRoles || {};
      this.comparisonPresets = this.initialReport.data.comparisonPresets || [];
      this.reportOverviewCustomText = this.initialReport.data.customText?.reportOverview ?? "";
      this.setComparableAssessments();

      this.$nextTick(() => {
        this.settingInitialValues = false;
      });
    }
  }

  async saveReport() {
    this.saving = true;

    if (this.editMode) {
      await this.updateProjectReport({
        reportId: this.initialReport?.identifier || "",
        name: this.reportName,
        description: this.reportDescription,
        assessmentIds: this.selectedAssessmentIds,
        data: this.createReportData(),
      });
      this.generatedReportId = this.initialReport?.identifier || "";
    } else {
      const projectReport: CreateProjectReport = {
        name: this.reportName,
        description: this.reportDescription,
        reportVersion: this.reportVersion,
        reportType: this.reportType,
        scoringVersion: this.scoringVerison,
        assessmentIds: this.selectedAssessmentIds,
        data: this.createReportData(),
      };
      this.generatedReportId = await this.systemSaveProjectReport({
        projectId: this.project.identifier,
        projectReport,
      });
    }

    await this.getProjects().finally(() => {
      this.saving = false;
      this.close();
    });
  }

  createReportData(): Record<string, unknown> {
    const traitWeights = {} as Record<string, number>;
    Object.values(TRAITS).forEach((trait) => (traitWeights[trait] = 3));

    const traitPriorities = {} as Record<string, number>;
    this.criticalCompetencies.forEach((trait) => (traitPriorities[trait] = 2));
    this.importantCompetencies.forEach((trait) => (traitPriorities[trait] = 1));
    this.supportingCompetencies.forEach((trait) => (traitPriorities[trait] = 0));

    const selected = new Set(this.selectedAssessmentIds);
    function pick<T>(record: Record<string, T>): Record<string, T> {
      return Object.keys(record).reduce((acc, id) => {
        if (selected.has(id) && record[id] !== undefined && record[id] !== null && String(record[id]).length > 0) acc[id] = record[id];
        return acc;
      }, {} as Record<string, T>);
    }

    return {
      traitPriorities,
      traitWeights,
      tags: pick(this.tags),
      tagColours: this.tagColours,
      candidateRoles: pick(this.candidateRoles),
      comparisonPresets: this.comparisonPresets,
      customText: {
        reportOverview: this.reportOverviewCustomText,
      },
    };
  }

  mapAssessmentToSchemaAssessment(assessment: V2Assessment, score: SchemasV2InternalAssessmentsScoring): SchemasCandidateAssessment {
    return {
      assessmentId: assessment.identifier,
      candidateName: assessment.candidate.name,
      isComplete: assessment.state === AssessmentState.ScoringComplete,
      project: assessment.projectId,
      organization: assessment.company,
      competencyScores: score.competencyScores,
      competencies: score.competencies,
      traitScores: score.traitScores,
      readinessScore: score.readiness,
      dateCompleted: assessment.dateCompleted,
    };
  }

  mapBenchmarkToSchemaBenchmark(benchmark: Benchmark): AdminSchemaBenchmark {
    return {
      alwaysShow: benchmark.default,
      benchmarkId: benchmark.identifier,
      colour: benchmark.colour,
      name: benchmark.name,
      competencyScores: benchmark.competencyScores,
      isAdmin: benchmark.isAdmin,
      organization: benchmark.orgId,
      traitScores: benchmark.traitScores,
      traitRanges: benchmark.traitRanges,
      dateCreated: "",
      lastUpdated: "",
    };
  }

  public colourDialogComplete(color: string): void {
    this.showColourPickerModal = false;
    if (this.selectedColourItem) {
      Vue.set(this.tagColours, this.selectedColourItem, color);
      Vue.nextTick(() => {
        this.tagColours = { ...this.tagColours }; // Force reactivity
        this.setComparableAssessments();
      });
    }
  }

  public colourDialogClose(): void {
    this.showColourPickerModal = false;
  }

  public colourDialogReset(): void {
    if (this.selectedColourItem) {
      Vue.delete(this.tagColours, this.selectedColourItem);
    }

    this.showColourPickerModal = false;
  }

  public showColourPicker(item: string): void {
    this.selectedColourItem = item;
    this.showColourPickerModal = true;
  }

  toFriendlyDate(date: string): string {
    const mDate = moment.utc(date).local();
    return mDate.format("MM/DD/YYYY");
  }

  close(): void {
    this.$emit("close");
    this.resetState();
  }

  resetState(): void {
    this.step = 1;
    this.editMode = false;
    this.initialReport = null;
    this.reportName = "";
    this.reportDescription = "";
    this.reportOverviewCustomText = "";
    this.searchText = "";
    this.candidateSelectedState = {};
    this.candidateRoles = {};
    this.tags = {};
    this.criticalCompetencies = [];
    this.importantCompetencies = [];
    this.supportingCompetencies = [...TRAITS];
    this.compariableBenchmarks = [];
    this.averageBenchmarks = [];
    this.compariableAssessments = [];
    this.comparisonPresets = [];
  }
}
